/**
 * Airport API client
 * Calls the airport-search Supabase Edge Function
 */

import { supabase } from '@/lib/supabase'
import type { AirportSearchResult, AirportSearchResponse } from '@/types/airports'

/**
 * Search airports using the full search endpoint
 * Returns all fields including coordinates for form auto-fill
 */
export async function searchAirports(
  query: string,
  limit = 20
): Promise<AirportSearchResult[]> {
  const { data, error } = await supabase.functions.invoke<AirportSearchResponse>(
    'airport-search',
    {
      body: { query, endpoint: 'search', limit },
    }
  )

  if (error) {
    console.error('Airport search error:', error)
    throw new Error(error.message || 'Failed to search airports')
  }

  if (!data?.status) {
    throw new Error(data?.error || 'Airport search failed')
  }

  return data.data || []
}

/**
 * Get full airport details using the search endpoint
 * Returns all available fields for a given code
 */
export async function getAirportDetails(
  code: string
): Promise<AirportSearchResult | null> {
  const { data, error } = await supabase.functions.invoke<AirportSearchResponse>(
    'airport-search',
    {
      body: { query: code, endpoint: 'search', limit: 1 },
    }
  )

  if (error) {
    console.error('Airport details error:', error)
    throw new Error(error.message || 'Failed to get airport details')
  }

  if (!data?.status || !data.data?.length) {
    return null
  }

  return data.data[0]
}

/**
 * Find airport by exact code (FAA or ICAO)
 */
export async function findAirportByCode(
  code: string
): Promise<AirportSearchResult | null> {
  const { data, error } = await supabase.functions.invoke<AirportSearchResponse>(
    'airport-search',
    {
      body: { query: code, endpoint: 'findByCode', limit: 1 },
    }
  )

  if (error) {
    console.error('Find airport by code error:', error)
    throw new Error(error.message || 'Failed to find airport')
  }

  if (!data?.status || !data.data?.length) {
    return null
  }

  return data.data[0]
}
